import React, { useEffect, useState } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, Col, Row } from 'react-bootstrap';
import { fetchHVACampaignWorkloadEvolution } from 'services/hvacallingmanagment.service';
import KPIEvolutionChart from 'components/charts/KPIEvolutionChart';
import { numberFormat } from 'helpers/utils';

interface KPIData {
  date: string;
  count: number;
}

interface APIResponse {
  filedate: string;
  clients: number;
}

interface CrmStatCardProps {
  icon: IconProp;
  color: string;
  label: string;
  title: string;
  title2?: string;
  campaignid: number;
}

const CampaignKPICard2 = ({
  icon,
  color,
  label,
  title,
  title2,
  campaignid
}: CrmStatCardProps) => {
  const [data, setData] = useState<KPIData[]>([]);
  const [averageValue, setAverageValue] = useState<number>(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchHVACampaignWorkloadEvolution(campaignid);
        const processedData: KPIData[] = (response as APIResponse[]).map(
          (item: APIResponse) => ({
            date: item.filedate.split('T')[0], // Extraer solo la fecha
            count: Number(item.clients) || 0
          })
        );

        setData(processedData);

        // Calcular la media de los valores sin decimales
        const totalValue = processedData.reduce(
          (sum, item) => sum + item.count,
          0
        );
        const average =
          processedData.length > 0
            ? Math.round(totalValue / processedData.length) // Redondear la media
            : 0;
        setAverageValue(average);
      } catch (error) {
        console.error('Error fetching campaign workload evolution:', error);
      }
    };

    fetchData();
  }, [campaignid]);

  const formattedValue = title.toLowerCase().includes('time')
    ? (() => {
        if (averageValue == null) return '0'; // Retorna un valor predeterminado si averageValue es nulo o indefinido
        const hours = Math.floor(averageValue);
        const minutes = Math.round((averageValue - hours) * 60);
        return `${hours}h:${minutes}m`;
      })()
    : averageValue != null
    ? numberFormat(Math.floor(averageValue))
    : '0'; // Retorna un valor predeterminado si averageValue es nulo o indefinido

  return (
    <Card className="mt-2 mb-4 me-4" style={{ minWidth: '15rem' }}>
      <Card.Body className="p-3 pt-0 pb-0">
        <Row className="mb-0 border-bottom">
          <Col
            className="d-flex align-items-center"
            xs={7}
            sm={7}
            md={7}
            xl={7}
            xxl={7}
          >
            <div className={`d-flex pt-1`}>
              <FontAwesomeIcon
                icon={icon}
                className={`text-${color} fs-6 z-1 ms-2`}
              />
            </div>
            <p className="text-body-tertiary fs-10 mb-0 ms-2 mt-2">
              {label} <br />
              <span className="p-0 fs-8 fw-bold lh-1 text-body-emphasis">
                {title}
                <span className="fs-10">{title2}</span>
              </span>
            </p>
          </Col>
          <Col
            className={`text-${color} mt-2 fs-7 fw-bold mb-0 pt-1 text-end`}
            xs={5}
            sm={5}
            md={5}
            xl={5}
            xxl={5}
          >
            {formattedValue} <span className="fs-9">avg.</span>
          </Col>
        </Row>
        <Row className="mb-0 mt-0 pt-2">
          <div className="h-100">
            <KPIEvolutionChart data={data} />
          </div>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default CampaignKPICard2;
